/**
 * /reviews — list of PR reviews run by the Sherlock Reviewer agent.
 *
 * Layout (top → bottom):
 *   1. Page header + "Manage repos →" link to the /reviews/repos sub-route
 *      (not a nav item — same pattern as /requests/new off /requests).
 *   2. Filter bar: status chips (All/Queued/Running/Done/Error), verdict
 *      chips (All/Clean/Concerns/Serious), repo Select (populated from the
 *      registry). State persists in the URL via useSearchParams so refresh
 *      + back-button restore the view.
 *   3. Paginated table: repo, PR (# + title, links to GitHub), trigger,
 *      verdict badge, findings count, cost, status chip (superseded rows
 *      dimmed + labelled), created (relative time).
 *   4. Empty state — distinguishes "no reviews yet" from "no rows match
 *      these filters".
 *
 * Wire conventions mirror InvestigationsListPage: params serialize to the
 * URL only when non-default; a filter change resets to page 1.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { ReviewStatusChip, ReviewTriggerChip, ReviewVerdictBadge } from '@/components/ReviewBadges';
import { ApiError, reviews as reviewsApi } from '@/helpers/ApiHelper';
import { formatRelative } from '@/utils/formatRelative';
import type {
  PaginatedReviews,
  PrReviewSummary,
  ReviewRepoConfigOut,
  ReviewStatus,
  Verdict,
} from '@/types/review';

const PAGE_SIZE = 25;

type StatusFilter = 'all' | ReviewStatus;
type VerdictFilter = 'all' | Verdict;

const STATUS_CHIPS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'done', label: 'Done' },
  { value: 'error', label: 'Error' },
  { value: 'skipped', label: 'Skipped' },
];

const VERDICT_CHIPS: Array<{ value: VerdictFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'clean', label: 'Clean' },
  { value: 'concerns', label: 'Concerns' },
  { value: 'serious', label: 'Serious' },
];

// ─── URL state helpers ───────────────────────────────────────────────────────

interface FilterState {
  page: number;
  status: StatusFilter;
  verdict: VerdictFilter;
  repo: string;
}

function readFiltersFromUrl(params: URLSearchParams): FilterState {
  const status = params.get('status');
  const verdict = params.get('verdict');
  return {
    page: Math.max(1, Number(params.get('page')) || 1),
    status:
      status === 'queued' ||
      status === 'running' ||
      status === 'done' ||
      status === 'error' ||
      status === 'skipped'
        ? status
        : 'all',
    verdict:
      verdict === 'clean' || verdict === 'concerns' || verdict === 'serious' ? verdict : 'all',
    repo: params.get('repo') ?? '',
  };
}

function filtersToUrl(state: FilterState): URLSearchParams {
  const next = new URLSearchParams();
  if (state.page > 1) next.set('page', String(state.page));
  if (state.status !== 'all') next.set('status', state.status);
  if (state.verdict !== 'all') next.set('verdict', state.verdict);
  if (state.repo) next.set('repo', state.repo);
  return next;
}

function hasActiveFilters(state: FilterState): boolean {
  return state.status !== 'all' || state.verdict !== 'all' || state.repo !== '';
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  state,
  repoOptions,
  onPatch,
}: {
  state: FilterState;
  repoOptions: string[];
  onPatch: (patch: Partial<FilterState>) => void;
}) {
  return (
    <Stack spacing={1.5} sx={{ mb: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        role="radiogroup"
        aria-label="Filter reviews by status"
        flexWrap="wrap"
        useFlexGap
      >
        {STATUS_CHIPS.map((chip) => {
          const active = state.status === chip.value;
          return (
            <Chip
              key={chip.value}
              label={chip.label}
              role="radio"
              aria-checked={active}
              clickable
              onClick={() => onPatch({ status: chip.value, page: 1 })}
              variant={active ? 'filled' : 'outlined'}
              color={active ? 'primary' : 'default'}
              sx={{ minHeight: 32 }}
            />
          );
        })}
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Stack
          direction="row"
          spacing={1}
          role="radiogroup"
          aria-label="Filter reviews by verdict"
          flexWrap="wrap"
          useFlexGap
        >
          {VERDICT_CHIPS.map((chip) => {
            const active = state.verdict === chip.value;
            return (
              <Chip
                key={chip.value}
                label={chip.label}
                role="radio"
                aria-checked={active}
                clickable
                onClick={() => onPatch({ verdict: chip.value, page: 1 })}
                variant={active ? 'filled' : 'outlined'}
                color={active ? 'primary' : 'default'}
                sx={{ minHeight: 32 }}
              />
            );
          })}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Select
          size="small"
          value={state.repo}
          onChange={(e) => onPatch({ repo: e.target.value, page: 1 })}
          displayEmpty
          sx={{ minWidth: 220 }}
          inputProps={{ 'aria-label': 'Filter by repo' }}
        >
          <MenuItem value="">All repos</MenuItem>
          {repoOptions.map((repo) => (
            <MenuItem key={repo} value={repo}>
              {repo}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function ReviewRow({ row, onOpen }: { row: PrReviewSummary; onOpen: (id: string) => void }) {
  const prUrl = `https://github.com/${row.repo}/pull/${row.pr_number}`;
  return (
    <TableRow
      hover
      onClick={() => onOpen(row.id)}
      sx={{
        cursor: 'pointer',
        opacity: row.superseded ? 0.6 : 1,
      }}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {row.repo}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
              #{row.pr_number}
            </Box>
            {row.pr_title}
          </Typography>
          <Tooltip title="Open PR on GitHub">
            <Box
              component="a"
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open PR #${row.pr_number} on GitHub`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 44,
                minHeight: 44,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>
        <ReviewTriggerChip trigger={row.trigger} />
      </TableCell>
      <TableCell>
        <ReviewVerdictBadge verdict={row.verdict} />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{row.findings_count}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          ${row.cost_usd.toFixed(3)}
        </Typography>
      </TableCell>
      <TableCell>
        <ReviewStatusChip status={row.status} superseded={row.superseded} />
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {formatRelative(row.created_at)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReviewsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);

  const [data, setData] = useState<PaginatedReviews | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [repoOptions, setRepoOptions] = useState<string[]>([]);

  const patchState = (patch: Partial<FilterState>): void => {
    const next: FilterState = { ...state, ...patch };
    setSearchParams(filtersToUrl(next), { replace: true });
  };

  // Fetch the registry once for the repo filter dropdown. Non-fatal on
  // failure — the rest of the page still works with an "All repos" only
  // option.
  useEffect(() => {
    let active = true;
    reviewsApi.repos
      .list()
      .then((resp) => {
        if (!active) return;
        setRepoOptions(resp.results.map((r: ReviewRepoConfigOut) => r.repo_full_name));
      })
      .catch(() => {
        if (!active) return;
        setRepoOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    reviewsApi
      .list({
        page: state.page,
        status: state.status === 'all' ? undefined : state.status,
        verdict: state.verdict === 'all' ? undefined : state.verdict,
        repo: state.repo || undefined,
      })
      .then((resp) => {
        if (!active) return;
        setData(resp);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const msg =
          err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : 'Unknown error';
        setError(msg);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [state.page, state.status, state.verdict, state.repo]);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const filtersActive = hasActiveFilters(state);

  const clearFilters = (): void => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'primary.main' }}>
              reviews
            </Typography>
            {data && (
              <Typography variant="caption" color="text.secondary">
                {data.count} review{data.count !== 1 ? 's' : ''}
                {filtersActive ? ' · filtered' : ''}
              </Typography>
            )}
          </Box>
          <Button
            component={RouterLink}
            to="/reviews/repos"
            variant="outlined"
            startIcon={<SettingsOutlinedIcon />}
            sx={{ minHeight: 44 }}
          >
            Manage repos
          </Button>
        </Stack>

        <FilterBar state={state} repoOptions={repoOptions} onPatch={patchState} />

        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}

        {loading && !data ? (
          <Box role="status" aria-label="Loading reviews" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : data && data.results.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {filtersActive ? 'No reviews match these filters.' : 'No PR reviews yet.'}
            </Typography>
            {filtersActive && (
              <Button onClick={clearFilters} sx={{ mt: 1.5, minHeight: 44 }}>
                Clear filters
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" aria-label="PR reviews">
              <TableHead>
                <TableRow>
                  <TableCell>Repo</TableCell>
                  <TableCell>PR</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Verdict</TableCell>
                  <TableCell align="right">Findings</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.results.map((row) => (
                  <ReviewRow key={row.id} row={row} onOpen={(id) => navigate(`/reviews/${id}`)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data && data.count > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Pagination
              count={totalPages}
              page={state.page}
              onChange={(_, p) => patchState({ page: p })}
              color="primary"
              shape="rounded"
              size="small"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
